import React from 'react'
import freeTrialImg from '@assets/images/Free_trial_green.png'
import andreaImg from '@assets/images/homepage-testimonials/andrea.png'
import eliotImg from '@assets/images/homepage-testimonials/eliot.png'
import mikhailImg from '@assets/images/homepage-testimonials/mikhail.png'
import { useUser } from '@features/auth/hooks'
import { PlanCard, QuestionAccordion } from '@features/pricing/components'
import { formatPrice } from '@features/pricing/utils/formatPrice'
import darkTheme from '@styles/themes/dark-theme'
import lightTheme from '@styles/themes/light-theme'
import ReactPlayer from 'react-player'
import {
  usePaymentCreateTeacherAiCheckoutMutation,
  usePaymentCreateTeacherCheckoutMutation,
  usePaymentGetStripeAccountByUserIdQuery,
  usePaymentGetTeacherSubscriptionPlansQuery,
} from '@app/services/auto-generated/payment'
import {
  Avatar,
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@common/components'
import { REGISTER_COMPANY_ROUTE } from '@common/constants/routes'
import { thumbnails } from '@common/constants/thumbnails'
import { useToggle, useTranslation } from '@common/hooks'
import { StarIcon, ThumbTestimonialsIcon } from '@common/icons'
import { GlobalStyles, ThemeProvider } from '@common/utils/styles'
import { useBlocker } from '@lib/router'
import { FeatureSection } from './components/features-section'
import FreeTrialModal from './components/free-trial-modal'
import PageAlmostReadyModal from './components/page-almost-ready-modal'
import SoldOutModal from './components/sold-out-modal'
import TeacherCurrentlyEditingModal from './components/teacher-currently-editing-modal'
import { UnauthHomepageModal } from './components/unauth-homepage-modal'
import { FlotingButton } from './display-home-teacher.styles'

// import IframeResizer from 'iframe-resizer-react'

interface Feature {
  title: string
  description: string
  url: string
}

interface DisplayHomeTeacherProps {
  homeTeacher: {
    url: string
    name: string
    welcome_message: string
    video_url: string
    thumbnail: string
    price: number
    features: Feature[]
    visible_prices: number[]
    expected_days: number
    profile_pic: string
    user_id: number
    color: string
    faq_color: string
    ai_price: number
  }
}

interface HomeHeaderProps {
  thumbnail: string
  teacherImg: string
}

interface HomeVideoProps {
  videoUrl: string
}

interface ErrorResponse {
  data: {
    detail: {
      error_code: string
    }
  }
}

export const DisplayHomeTeacher = (props: DisplayHomeTeacherProps) => {
  const {
    url,
    name,
    welcome_message,
    video_url,
    thumbnail,
    price,
    features,
    visible_prices: options,
    expected_days,
    profile_pic: teacherImg,
    user_id,
    color,
    faq_color,
    ai_price,
  } = props.homeTeacher
  const { user } = useUser()
  const { t } = useTranslation('homepage')
  const { data } = usePaymentGetTeacherSubscriptionPlansQuery(
    { url: url },
    { refetchOnMountOrArgChange: true },
  )

  const [createCheckout, { isLoading }] = usePaymentCreateTeacherCheckoutMutation()
  const [createAiCheckout] = usePaymentCreateTeacherAiCheckoutMutation()
  const { data: stripeAccount } = usePaymentGetStripeAccountByUserIdQuery({ userId: user_id })
  const { open, handleClose, handleOpen } = useToggle(false)
  const {
    open: openSoldOut,
    handleClose: handleCloseSoldOut,
    handleOpen: handleOpenSoldOut,
  } = useToggle(false)
  const {
    open: openTeacherEdition,
    handleClose: handleCloseTeacherEdition,
    handleOpen: handleOpenTeacherEdition,
  } = useToggle(false)
  const {
    open: openUnauthHomepage,
    handleClose: handleCloseUnauthHomepage,
    handleOpen: handleOpenUnauthHomepage,
  } = useToggle(false)
  const {
    open: openFreeTrialModal,
    handleClose: handleCloseFreeTrialModal,
    handleOpen: handleOpenFreeTrialModal,
  } = useToggle(false)
  const [noRecording, setNoRectording] = React.useState(5)
  const getPlanId = (ai: boolean, recordingsNumber: number): string => {
    const planType = recordingsNumber === 15 ? 'fifteen' : 'five'
    const planDuration = '_ten'
    // const planDuration = switchedOptDuration ? '_thirty' : '_ten'
    const key = ai ? `ai_${planType}${planDuration}_plan` : `${planType}${planDuration}_plan`
    if (data) {
      return data[key] || ''
    } else return ''
  }
  React.useEffect(() => {
    if (
      user &&
      user?.id === user_id &&
      stripeAccount &&
      stripeAccount.charges_enabled === false &&
      stripeAccount.id !== null
    ) {
      handleOpen()
    }
  }, [user, stripeAccount])

  React.useEffect(() => {
    if (window.location.hash === '#pricing') {
      const element = document.getElementById('pricing')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [])

  const handleStart = async () => {
    if (!user) {
      handleOpenUnauthHomepage()
    } else {
      handleOpenFreeTrialModal()
    }
  }
  const handleCheckout = async (planId: string, freeTrial: boolean) => {
    try {
      const { url } = await createCheckout({
        createTeacherCheckoutRequest: {
          subscription_id: planId,
          cancel_url: window.location.href,
          free_trial: freeTrial,
        },
      }).unwrap()
      window.location.href = url
    } catch (error: unknown) {
      const err = error as ErrorResponse
      console.log(error)
      if (
        err.data?.detail?.error_code === 'EXC_TEACHER_DIDNT_FINISH_STRIPE_ONBOARDING' &&
        user?.status === 'STUDENT'
      ) {
        handleOpenTeacherEdition()
      }
      if (
        err.data?.detail?.error_code === 'EXC_TEACHER_MAX_SUBS_REACHED' &&
        user?.status === 'STUDENT'
      ) {
        handleOpenSoldOut()
      }
    }
  }
  const handleCheckoutAi = async (planId: string) => {
    if (!user) {
      handleOpenUnauthHomepage()
    } else {
      try {
        const { url } = await createAiCheckout({
          createTeacherAiCheckoutRequest: {
            subscription_id: planId,
            cancel_url: window.location.href,
          },
        }).unwrap()
        window.location.href = url
      } catch (error: unknown) {
        const err = error as ErrorResponse
        if (
          err.data?.detail?.error_code === 'EXC_TEACHER_DIDNT_FINISH_STRIPE_ONBOARDING' &&
          user?.status === 'STUDENT'
        ) {
          handleOpenTeacherEdition()
        }
        if (
          err.data?.detail?.error_code === 'EXC_TEACHER_MAX_SUBS_REACHED' &&
          user?.status === 'STUDENT'
        ) {
          handleOpenSoldOut()
        }
      }
    }
  }

  const planAi = {
    title: 'Lite',
    planImg: (
      <Avatar
        src='https://i.ibb.co/51BP2w2/istockphoto-1346150003-612x612-1.png'
        sx={{ width: '70px', height: '70px' }}
      />
    ),
    price: '$' + (ai_price ? formatPrice(ai_price) : 9),
    detail: 'per month',
    highlighted: false,
    subprice: '',
    description: [
      `Access to exclusive video courses and exercises from ${name}`,
      'AI feedback on grammar, vocabulary, pronunciation and fluency',
      'Live speaking practice 3 times a week',
      'IELTS Speaking mock tests',
      'Try for free for 7 days, cancel anytime',
    ],
    callToAction: (
      <FlotingButton onClick={() => handleCheckoutAi(getPlanId(true, 5))} fullWidth>
        {t('homepage.start_now', 'Start for Free')}
      </FlotingButton>
    ),
  }
  const optionsCount = (array: number[]) => {
    let count = 0
    const found: number[] = []
    for (let i = 0; i < array.length; i++) {
      if (
        (array[i] === 999 || array[i] === 510 || array[i] === 1510) &&
        !found.includes(array[i])
      ) {
        count++
        found.push(array[i])
      }
    }
    return count
  }
  console.log(optionsCount(options))
  const pricingCard = (premium?: boolean) => {
    const handleClick = () => {
      handleStart()
      setNoRectording(!premium ? 5 : 15)
    }
    return {
      title: !premium ? 'Basic' : 'Premium',
      price: '$' + formatPrice(price * (!premium ? 1 : 3 * 0.8)),
      planImg: <Avatar src={teacherImg} sx={{ width: '70px', height: '70px' }} />,
      detail: 'per month',
      highlighted: premium ?? false,
      subprice: '',
      description: [
        `Access to exclusive video courses and exercises from ${name}`,
        `${name} gives you personal voice feedback and corrections`,
        !premium
          ? 'Personal voice feedback for up to 5 recordings every month'
          : 'Personal voice feedback for up to 15 recordings every month',
        'AI feedback on grammar and pronunciation included',
        !premium ? 'Live speaking practice 3 times a week' : 'Daily live speaking practice',
        'IELTS speaking mock tests',
        'Try for free for 7 days, cancel anytime',
      ],
      callToAction: (
        <FlotingButton onClick={handleClick} fullWidth isPremium={premium}>
          {t('homepage.start_now', 'Start for Free')}
        </FlotingButton>
      ),
    }
  }
  const corporateCard = () => {
    return {
      title: 'Corporate Sponsored Membership',
      planImg: <Avatar src={teacherImg} sx={{ width: '70px', height: '70px' }} />,
      price: '',
      detail: '',
      highlighted: true,
      subprice: '',
      description: [
        'Daily Speaking Practice',
        'Personal AI Feedback',
        'Progress Tracking with Certificates',
      ],
      callToAction: (
        <FlotingButton component={Link} href={REGISTER_COMPANY_ROUTE} fullWidth isPremium={true}>
          {t('homepage.get_started', 'Get Started')}
        </FlotingButton>
      ),
    }
  }
  const commonlyQuestions = [
    {
      question: t('homepage.question1', 'Which browsers are supported?'),
      answer: (
        <Typography sx={{ fontWeight: '300', color: 'white' }}>
          {t(
            'homepage.answer1',
            ' Chrome is the primary browser supported. Safari, Mozilla, Edge, Opera and Brave may require operation in private browser mode.',
          )}
        </Typography>
      ),
    },
    {
      question: t('homepage.question2', 'Is it possible to use I Speak on mobile?'),
      answer: (
        <Typography sx={{ fontWeight: '300', color: 'white' }}>
          {t(
            'homepage.answer2',
            'I Speak is designed to run on both PC and mobile browsers. However, we recommend the use of laptop or PC in order to take advantage of full screen visibility.',
          )}
        </Typography>
      ),
    },
  ]
  const defaultFeatures = [
    {
      title: t('homepage.feature-title-1', 'Answer Questions'),
      description: `${t('homepage.let', 'Let ')}${name} ${t(
        'homepage.feature-description-1',
        'lead you through relevant speaking exercises, so you can master grammar and pronunciation while responding to every topic.',
      )}`,
      url: 'https://youtu.be/un-82TC2uNg',
    },
    {
      title: t('homepage.feature-title-2', 'Receive Corrections'),
      description: `${t(
        'homepage.feature-description-2.1',
        'Receive grammar and pronunciation corrections including personal feedback from',
      )} ${name}. ${t(
        'homepage.feature-description-2.2',
        'Test yourself with an IELTS speaking exam simulator.',
      )}`,
      url: 'https://youtu.be/ud9vA323zGk',
    },
    {
      title: t('homepage.feature-title-3', 'Practice Live'),
      description: t(
        'homepage.feature-description-3',
        'Live practice sessions facilitated by a native speaker teacher! You can ask your grammar and pronunciation questions here.',
      ),
      url: 'https://youtu.be/FfiL7HjKfv8',
    },
  ]

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !user &&
      !(openUnauthHomepage || blocker.state === 'blocked') &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  return (
    <ThemeProvider theme={isDarkColor(color) ? darkTheme : lightTheme}>
      <GlobalStyles
        styles={{
          body: {
            backgroundColor: color ? color : '#fff',
            '.custom-menu-icon': {
              color: isDarkColor(color) ? '#FFF' : '#464646' /* Customize the color as needed */,
            },
          },
        }}
      />
      <Paper elevation={0} sx={{ borderRadius: 0, backgroundColor: color }}>
        <FreeTrialModal
          open={openFreeTrialModal}
          onClose={handleCloseFreeTrialModal}
          teacher_name={name}
          onClick={handleCheckout}
          getPlanId={getPlanId}
          recordingsNumber={noRecording}
        />
        <PageAlmostReadyModal open={open} onClose={handleClose} />
        <SoldOutModal open={openSoldOut} onClose={handleCloseSoldOut} teacher_name={name} />
        <TeacherCurrentlyEditingModal
          open={openTeacherEdition}
          onClose={handleCloseTeacherEdition}
          teacher_name={name}
        />
        <UnauthHomepageModal
          open={openUnauthHomepage || blocker.state === 'blocked'}
          onClose={() => {
            if (blocker.state === 'blocked') {
              blocker.reset()
            }
            handleCloseUnauthHomepage()
          }}
        />
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
        <HomeHeader thumbnail={thumbnail} teacherImg={teacherImg} />
        <Container sx={{ mt: 7 }}>
          <Typography component='h1' variant='h4' textAlign='center' fontWeight='bold'>
            {name === '' ? '[Name]' : name}
          </Typography>
          <Typography
            component='h2'
            variant='h5'
            color='text.secondary'
            textAlign='center'
            fontWeight='300'
            sx={{ mt: 1 }}
          >
            {welcome_message}
          </Typography>
          <HomeVideo videoUrl={video_url} />
          <Box sx={{ py: 15 }}>
            <Typography variant='h3' sx={{ mb: 6 }} textAlign='center'>
              {t('homepage.how-it-works', 'How it works')}
            </Typography>
            {features && features?.length > 0
              ? features?.map((feature: Feature, index) => (
                  <FeatureSection
                    key={index}
                    title={feature.title}
                    description={feature.description}
                    url={feature.url}
                    reverse={index % 2 === 1}
                    sx={{ mb: index !== features.length - 1 ? 12 : undefined }}
                  />
                ))
              : defaultFeatures?.map((feature, index) => (
                  <FeatureSection
                    key={index}
                    title={feature.title}
                    description={feature.description}
                    url={feature.url}
                    reverse={index % 2 === 1}
                    sx={{ mb: index !== defaultFeatures.length - 1 ? 12 : undefined }}
                  />
                ))}
          </Box>
          <Stack id='pricing' alignItems='center' spacing={1} sx={{ mb: 2 }}>
            {optionsCount(options) !== 0 ? (
              <>
                <Typography variant='h3' sx={{ mb: 4 }} textAlign='center'>
                  {t('homepage.pricing', 'Pricing')}
                </Typography>
                <Grid
                  container
                  sx={{
                    maxWidth:
                      options?.includes(999) && optionsCount(options) === 3 ? '950px' : '750px',
                  }}
                  columnSpacing={{ xs: 0, md: 2 }}
                  rowSpacing={1}
                >
                  {options?.includes(999) && (
                    <Grid item xs={12} md={12 / optionsCount(options)}>
                      <PlanCard {...planAi} icons />
                    </Grid>
                  )}
                  {options?.includes(510) && (
                    <Grid item xs={12} md={12 / optionsCount(options)}>
                      <PlanCard {...pricingCard()} icons />
                    </Grid>
                  )}
                  {options?.includes(1510) && (
                    <Grid item xs={12} md={12 / optionsCount(options)}>
                      <PlanCard {...pricingCard(true)} icons />
                    </Grid>
                  )}
                </Grid>
                <img
                  src={freeTrialImg}
                  alt={'Free trial'}
                  style={{
                    marginTop: '20px',
                    maxWidth: '90%',
                    width: '250px',
                    objectFit: 'contain',
                  }}
                />
              </>
            ) : (
              <PlanCard {...corporateCard()} icons />
            )}
          </Stack>
          <Testimonials />
        </Container>
        <Box sx={{ background: faq_color ? faq_color : '#5556C7', mt: 10, py: 6 }}>
          <Container maxWidth='lg'>
            <Grid container spacing={4}>
              <Grid item sm={4} xs={12}>
                <Typography
                  variant='h4'
                  sx={{ fontWeight: '550', color: isDarkColor(faq_color) ? '#FFF' : '#464646' }}
                >
                  {t('homepage.commenly', 'Commonly')} <br />
                  {t('homepage.asked_questions', 'asked questions')}
                </Typography>
              </Grid>
              <Grid item sm={8} xs={12}>
                <Stack>
                  {commonlyQuestions.map((question, key) => (
                    <QuestionAccordion
                      key={key}
                      question={question}
                      color={isDarkColor(faq_color) ? '#FFF' : '#464646'}
                    />
                  ))}
                  <QuestionAccordion
                    color={isDarkColor(faq_color) ? '#FFF' : '#464646'}
                    question={{
                      question: t('homepage.question_def', 'How quickly will I receive feedback?'),
                      answer: (
                        <Typography
                          sx={{
                            fontWeight: '300',
                            color: isDarkColor(faq_color) ? '#FFF' : '#464646',
                          }}
                        >
                          {t(
                            'homepage.answer.def1',
                            'You will receive feedback from the AI within minutes, and from',
                          )}{' '}
                          <span style={{ fontWeight: '600' }}>{name}</span>{' '}
                          {t('homepage.within', 'within')} {expected_days ? expected_days : 2}{' '}
                          {t('homepage.days', 'day(s)')}
                        </Typography>
                      ),
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Paper>
    </ThemeProvider>
  )
}

const HomeHeader = (props: HomeHeaderProps) => {
  const { thumbnail, teacherImg } = props
  return (
    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', maxHeight: '320px' }}>
      <img
        src={thumbnail ? thumbnail : thumbnails[0]}
        alt=''
        height='100%'
        width='100%'
        style={{ position: 'absolute', top: 0, objectFit: 'cover' }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '0',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Avatar
          src={teacherImg}
          sx={{
            width: '90px',
            height: '90px',
            transform: 'translate(0,50%)',
          }}
        />
      </Box>
    </Box>
  )
}

const HomeVideo = (props: HomeVideoProps) => {
  const { videoUrl } = props
  return (
    <Box
      sx={{
        maxWidth: '580px',
        width: '100%',
        position: 'relative',
        aspectRatio: '16/9',
        borderRadius: '11px',
        background: 'rgba(230,230,230,0.7)',
        overflow: 'hidden',
        mx: 'auto',
        my: 5,
      }}
    >
      <ReactPlayer
        volume={0.8}
        url={videoUrl ? videoUrl : 'https://youtu.be/gAsE8TUvi5c'}
        controls
        width='100%'
        height='100%'
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
    </Box>
  )
}

const Testimonials = () => {
  const { t } = useTranslation('homepage')
  const testimonials = [
    {
      name: 'Andrea S.',
      location: 'From Peru, living in Spain',
      feedback:
        'I am always so excited to see new tasks every time for me to practice speaking. They are fun and I love the corrections because I become confident already!',
      avatar: andreaImg,
    },
    {
      name: 'Elliot K.',
      location: 'From France, living in France',
      feedback:
        "Hello, I come from France. I'm studying and working as a salesman on the side. I really enjoyed to do live session because you can share good time with people from all over the world and that is a nice way to practice. I highly recommend.",
      avatar: eliotImg,
    },
    {
      name: 'Mikhail H.',
      location: 'From Belarus, living in Poland',
      feedback:
        'I have never seen something like this for English. It helps me build my career, express my thoughts, and find a helpful community in speaking practice. Due to the feedback I get new vocabulary and jargon so that I can learn more English. Thanks so much again and again.',
      avatar: mikhailImg,
    },
  ]
  return (
    <>
      <Typography variant='h4' sx={{ mt: 10, mb: 4 }} textAlign='center'>
        {t('homepage.testimonials', 'Testimonials')}
      </Typography>
      <Grid container columnSpacing={{ xs: 0, md: 2 }} rowSpacing={1}>
        {testimonials?.map((testimonial, index) => (
          <Grid key={index} item xs={12} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                mx: 'auto',
                maxWidth: '375px',
                width: '100%',
                height: '270px',
                borderRadius: '15px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' alignItems='center'>
                  <StarIcon fontSize={'small'} sx={{ color: '#3E66DF' }} />
                  <StarIcon fontSize={'small'} sx={{ color: '#3E66DF' }} />
                  <StarIcon fontSize={'small'} sx={{ color: '#3E66DF' }} />
                  <StarIcon fontSize={'small'} sx={{ color: '#3E66DF' }} />
                  <StarIcon fontSize={'small'} sx={{ color: '#3E66DF' }} />
                </Stack>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <ThumbTestimonialsIcon />{' '}
                  <span style={{ marginLeft: '3px', fontSize: '14px', fontWeight: 'bold' }}>
                    Testimonial
                  </span>
                </span>
              </Stack>
              <Stack sx={{ flex: 1, justifyContent: 'center' }}>
                <Typography variant='body2'>{testimonial.feedback}</Typography>
              </Stack>
              <Stack direction='row' spacing={1}>
                <Avatar sx={{ width: '35px', height: '35px' }} src={testimonial.avatar} />
                <Stack>
                  <Typography variant='body2' fontWeight='bold' sx={{ mb: -0.5 }}>
                    {testimonial.name}
                  </Typography>
                  <Typography variant='caption'>{testimonial.location}</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  )
}

const isDarkColor = (color: string) => {
  if (color) {
    const hexColor = color.slice(1)
    const r = parseInt(hexColor.slice(0, 2), 16)
    const g = parseInt(hexColor.slice(2, 4), 16)
    const b = parseInt(hexColor.slice(4, 6), 16)

    // Calculate luminance using the relative luminance formula
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

    // Adjust the threshold as needed
    const isDark = luminance < 128

    return isDark
  }
}
